import React, { useCallback, useEffect, useMemo } from 'react'
import { usePayments } from '@verzekeringapp-mono/shared/src/hooks/use_payments'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { getPaymentsByYear as getPaymentsByYearSelector, getPaymentsIsLoading } from '@verzekeringapp-mono/shared/src/state/selectors/payments'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingIndicator from '~/components/LoadingIndicator'

const PaymentsScreen: React.FC = () => {
  const { getPayments } = usePayments()

  useEffect(() => {
    getPayments()
  }, [])

  const paymentsByYear = useAppSelector(getPaymentsByYearSelector)
  const isLoading = useAppSelector(getPaymentsIsLoading)

  const sortedPaymentsByYear = useMemo(() => Object.keys(paymentsByYear)
    .sort((a, b) => +b - +a), [paymentsByYear])

  const getNumberFromString = useCallback((string: string) => {
    const number = Number(string)

    if (number) {
      return number
    }

    return 0
  }, [])

  const getOutstandingAmount = useCallback((payment: { outstanding: string }) =>
    getNumberFromString(payment.outstanding), [getNumberFromString])

  const getCollectionAmount = useCallback((payment: { collectionAmount: string }) =>
    getNumberFromString(payment.collectionAmount), [getNumberFromString])

  return (
    <ScreenWrapper>
      <div>
        {isLoading && (
          <div className='flex justify-center items-center'>
            <LoadingIndicator />
          </div>
        )}

        {!isLoading && sortedPaymentsByYear.length === 0
          ? (
            <div className='flex justify-center items-center'>
              <p>Je hebt nog geen betalingen</p>
            </div>
          )
          : sortedPaymentsByYear.map((year) => (
            <div key={year}>
              <p className='py-4'>{year}</p>

              {paymentsByYear[year].map((payment) => (
                <div
                  key={payment.invoiceNumber}
                  className='mb-4 rounded bg-surface-card p-4'
                >
                  <div className='flex justify-between'>
                    <div className='flex flex-col gap-2'>
                      {payment.invoiceType === 'Prolongatie'
                        ? 'Betaling'
                        : payment.invoiceType}

                      <div className='flex gap-2 items-center'>
                        <FontAwesomeIcon
                          icon={['fal', 'calendar']}
                          className='text-icon-secondary'
                        />

                        <p className='text-base light'>
                          {payment.bookDate}
                        </p>
                      </div>
                    </div>

                    <div className='flex gap-4'>
                      <p
                        className={getOutstandingAmount(payment) !== 0
                          ? 'text-status-warning-foreground'
                          : 'text-status-success-foreground'}
                      >
                        {getOutstandingAmount(payment) !== 0 ? 'Open' : 'Betaald'}
                      </p>

                      <p>
                        {Intl.NumberFormat('nl-NL', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(getOutstandingAmount(payment) !== 0 ? getOutstandingAmount(payment) : getCollectionAmount(payment))}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </ScreenWrapper>
  )
}

export default PaymentsScreen
